import { TF } from 'react-translator-component';

import {
  AuditEvent,
  AuditEventType,
  AuditResult,
} from '../../../../types/responses/Interoperability';

/**
 * Rótulos da trilha de auditoria (MV2-1718).
 *
 * Usa TF e não T onde o retorno é declarado string: o T devolve o array de nós do
 * react-html-parser, não texto — funciona ao renderizar direto em JSX, mas mente
 * no tipo e estoura na primeira operação de string. O TF devolve T(...)[0].
 *
 * A API devolve `event_label` e `result_label` já escritos, mas em pt-BR fixo no
 * enum do serviço — o dashboard atende cinco idiomas, e um operador colombiano
 * veria "Convite gerado". Aqui o rótulo sai do `event_type`, que é estável, e o
 * texto do servidor fica só como reserva para um tipo que a tela ainda não
 * conheça.
 */

const EVENT_LABELS: Record<AuditEventType, string> = {
  INVITE_GENERATED: 'Invite generated',
  INVITE_REVOKED: 'Invite revoked',
  INVITE_EXPIRED: 'Invite expired',
  INVITE_TOKEN_USED: 'Token used',
  INVITE_TOKEN_ATTEMPT_FAILED: 'Invalid token attempt',
  CONTRACT_SCOPE_CHANGED: 'Scope changed',
  CONTRACT_SUBACCOUNT_INHERITED: 'Subaccount included automatically',
  CONTRACT_SUSPENDED: 'Connection suspended',
  CONTRACT_REACTIVATED: 'Connection reactivated',
  CONTRACT_REVOKED: 'Connection revoked',
  SHARE_TOKEN_GENERATED: 'Account token generated',
  SHARE_TOKEN_RENEWED: 'Account token renewed',
  TENANT_ELIGIBILITY_ENABLED: 'Eligibility enabled',
  TENANT_ELIGIBILITY_DISABLED: 'Eligibility disabled',
};

const RESULT_LABELS: Record<AuditResult, string> = {
  SUCCESS: 'Success',
  BLOCKED: 'Blocked',
  FAILURE: 'Failure',
};

export const eventLabel = (event: AuditEvent): string => {
  const known = EVENT_LABELS[event.event_type];

  return known ? TF(known) : event.event_label || event.event_type;
};

export const resultLabel = (event: AuditEvent): string => {
  const known = RESULT_LABELS[event.result];

  return known ? TF(known) : event.result_label || event.result;
};

/** Tipos oferecidos no filtro, na ordem em que a trilha costuma ser lida. */
export const EVENT_TYPE_OPTIONS = (): { value: AuditEventType; label: string }[] =>
  (Object.keys(EVENT_LABELS) as AuditEventType[]).map((value) => ({
    value,
    label: TF(EVENT_LABELS[value]),
  }));

/** Cor do ponto por resultado, no mesmo código do resto do módulo. */
export const RESULT_STYLE: Record<AuditResult, { dot: string; fg: string; bg: string }> = {
  SUCCESS: { dot: '#26a35c', fg: '#1e7a45', bg: '#e7f6ed' },
  BLOCKED: { dot: '#c8352f', fg: '#b02525', bg: '#fdecec' },
  FAILURE: { dot: '#e0700f', fg: '#a8500f', bg: '#fdeee4' },
};

/**
 * Quem aparece na coluna Parceiro.
 *
 * Tentativa de token inválida não tem contraparte: o convite não foi
 * identificado, então não se sabe de quem partiu. Dizer "desconhecido" é mais
 * honesto que deixar a célula vazia, que parece falha de carregamento.
 */
export const counterpartLabel = (event: AuditEvent): string =>
  event.counterpart_name || (event.counterpart_tenant_id ? TF('unnamed tenant') : TF('unknown'));

/**
 * Detalhe legível do evento.
 *
 * O contador de tentativas vem como "3/5" e sozinho na tela não diz o que conta.
 * Nunca contém token: o gravador recusa detalhe com o prefixo do token.
 */
export const detailLabel = (event: AuditEvent): string | null => {
  if (!event.detail) {
    return null;
  }

  if (event.event_type === 'INVITE_TOKEN_ATTEMPT_FAILED') {
    return `${TF('attempt')} ${event.detail}`;
  }

  return event.detail;
};

/** "17/08/2026 16:41", como no protótipo. */
export const eventMoment = (event: AuditEvent): string => {
  if (!event.occurred_at) {
    return '—';
  }

  const moment = new Date(event.occurred_at);

  if (Number.isNaN(moment.getTime())) {
    return '—';
  }

  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${pad(moment.getDate())}/${pad(moment.getMonth() + 1)}/${moment.getFullYear()} ${pad(
    moment.getHours(),
  )}:${pad(moment.getMinutes())}`;
};
